/** Lookup tables built once from every module contract. Shared by server, web and agent. */
import type { EventTypeDef, FacetDef, JobTypeDef, ModuleContract, RanchSectionDef, StockKindDef, WorkspaceDef } from "./module";

export interface Registry {
  modules: ModuleContract[];
  module(id: string): ModuleContract | undefined;
  events: Map<string, EventTypeDef>;
  event(type: string): EventTypeDef | undefined;
  facets: FacetDef[];
  facetsForEvent(type: string): FacetDef[];
  jobs: Map<string, JobTypeDef>;
  workspaces: WorkspaceDef[];
  ranchSections: RanchSectionDef[];
  stockKinds: Map<string, StockKindDef>;
}

export function buildRegistry(modules: ModuleContract[]): Registry {
  const events = new Map<string, EventTypeDef>();
  const jobs = new Map<string, JobTypeDef>();
  const stockKinds = new Map<string, StockKindDef>();
  const facets: FacetDef[] = [];
  const byEvent = new Map<string, FacetDef[]>();
  const seenFacets = new Set<string>();
  for (const m of modules) {
    for (const e of m.events ?? []) {
      if (!e.type.startsWith(`${m.id}.`)) throw new Error(`Event type ${e.type} must start with its module id ${m.id}.`);
      if (events.has(e.type)) throw new Error(`Duplicate event type ${e.type}.`);
      events.set(e.type, e);
    }
    for (const j of m.jobs ?? []) {
      if (!j.type.startsWith(`${m.id}.`)) throw new Error(`Job type ${j.type} must start with its module id ${m.id}.`);
      jobs.set(j.type, j);
    }
    for (const s of m.stockKinds ?? []) stockKinds.set(s.kind, s);
    for (const facet of m.facets ?? []) {
      const key = `${facet.subject}:${facet.id}`;
      if (seenFacets.has(key)) throw new Error(`Duplicate facet ${key}.`);
      seenFacets.add(key);
      facets.push(facet);
      for (const type of facet.eventTypes) byEvent.set(type, [...(byEvent.get(type) ?? []), facet]);
    }
  }
  const byId = new Map(modules.map((m) => [m.id, m]));
  return {
    modules,
    module: (id) => byId.get(id),
    events,
    event: (type) => events.get(type),
    facets,
    facetsForEvent: (type) => byEvent.get(type) ?? [],
    jobs,
    workspaces: modules.flatMap((m) => m.workspaces ?? []),
    ranchSections: modules.flatMap((m) => m.ranchSections ?? []),
    stockKinds,
  };
}
